import { arrayMove } from '@dnd-kit/sortable';
import type { GroupBy } from '../../../components/work-items/types';
import type { BoardWorkItemRow } from './types';
import { midpointRank } from './midpointRank';

export const COLUMN_ID_PREFIX = 'col:';

export function columnDroppableId(stage: string): string {
  return `${COLUMN_ID_PREFIX}${stage}`;
}

export function parseColumnDroppableId(id: string): string | null {
  return id.startsWith(COLUMN_ID_PREFIX) ? id.slice(COLUMN_ID_PREFIX.length) : null;
}

export function epicGroupKey(parentId: string | null): string {
  return parentId ?? '~unassigned';
}

export function sortableGroupDroppableId(stage: string, groupKey: string): string {
  return `group:${stage}:${groupKey}`;
}

export function parseSortableGroupId(
  id: string
): { stage: string; groupKey: string } | null {
  if (!id.startsWith('group:')) return null;
  const rest = id.slice('group:'.length);
  const sep = rest.indexOf(':');
  if (sep === -1) return null;
  return { stage: rest.slice(0, sep), groupKey: rest.slice(sep + 1) };
}

function resolveDropTarget(
  cards: ReadonlyArray<BoardWorkItemRow>,
  overId: string
): { stage: string; groupKey: string | null; overCardId: string | null } | null {
  const columnStage = parseColumnDroppableId(overId);
  if (columnStage) {
    return { stage: columnStage, groupKey: null, overCardId: null };
  }

  const group = parseSortableGroupId(overId);
  if (group) {
    return { stage: group.stage, groupKey: group.groupKey, overCardId: null };
  }

  const overCard = cards.find((c) => c.id === overId);
  if (!overCard) return null;
  return {
    stage: overCard.status,
    groupKey: epicGroupKey(overCard.parent_work_item_id),
    overCardId: overId
  };
}

function containerGroupKey(
  groupBy: GroupBy,
  card: BoardWorkItemRow
): string | null {
  return groupBy === 'epic' ? epicGroupKey(card.parent_work_item_id) : null;
}

function cardsInContainer(
  cards: ReadonlyArray<BoardWorkItemRow>,
  stage: string,
  groupKey: string | null,
  visibleIds: ReadonlySet<string>,
  excludeId?: string
): BoardWorkItemRow[] {
  return cards
    .filter((c) => {
      if (c.status !== stage || !visibleIds.has(c.id)) return false;
      if (excludeId && c.id === excludeId) return false;
      if (groupKey === null) return true;
      return epicGroupKey(c.parent_work_item_id) === groupKey;
    })
    .sort((a, b) => a.rank - b.rank);
}

/** Ordered ids for a column container (flat or epic subgroup). */
export function containerItemIds(
  cards: ReadonlyArray<BoardWorkItemRow>,
  stage: string,
  groupKey: string | null,
  visibleIds: ReadonlySet<string>
): string[] {
  return cardsInContainer(cards, stage, groupKey, visibleIds).map((c) => c.id);
}

function interimRanksForOrder(ordered: ReadonlyArray<BoardWorkItemRow>): Map<string, number> {
  const map = new Map<string, number>();
  ordered.forEach((c, i) => map.set(c.id, (i + 1) * 1000));
  return map;
}

function applyContainerOrder(
  cards: ReadonlyArray<BoardWorkItemRow>,
  stage: string,
  groupKey: string | null,
  orderedIds: string[],
  activeId: string,
  visibleIds: ReadonlySet<string>
): BoardWorkItemRow[] {
  const orderedCards = orderedIds
    .map((id) => cards.find((c) => c.id === id))
    .filter((c): c is BoardWorkItemRow => c != null);

  const rankById = interimRanksForOrder(orderedCards);
  const active = cards.find((c) => c.id === activeId);

  return cards.map((c) => {
    if (c.id === activeId && active) {
      return {
        ...c,
        status: stage,
        rank: rankById.get(activeId) ?? c.rank
      };
    }
    if (c.status === stage && visibleIds.has(c.id)) {
      if (groupKey !== null && epicGroupKey(c.parent_work_item_id) !== groupKey) {
        return c;
      }
      const nextRank = rankById.get(c.id);
      if (nextRank !== undefined) return { ...c, rank: nextRank };
    }
    return c;
  });
}

/**
 * Live reorder during drag (dnd-kit multi-container pattern).
 * Returns a new cards array, or null when the move should be ignored.
 */
export function applyBoardDragOver(
  cards: ReadonlyArray<BoardWorkItemRow>,
  activeId: string,
  overId: string,
  options: {
    groupBy: GroupBy;
    visibleIds: ReadonlySet<string>;
  }
): BoardWorkItemRow[] | null {
  if (activeId === overId) return null;

  const active = cards.find((c) => c.id === activeId);
  if (!active || !options.visibleIds.has(activeId)) return null;

  const target = resolveDropTarget(cards, overId);
  if (!target) return null;

  const activeGroup = containerGroupKey(options.groupBy, active);
  let targetGroup = target.groupKey;

  if (target.overCardId === null && target.groupKey === null) {
    targetGroup = activeGroup;
  }

  if (
    options.groupBy === 'epic' &&
    active.status === target.stage &&
    activeGroup !== null &&
    targetGroup !== null &&
    activeGroup !== targetGroup &&
    target.overCardId !== null
  ) {
    return null;
  }

  const containerKey = targetGroup ?? activeGroup;
  const currentIds = containerItemIds(
    cards,
    active.status,
    activeGroup,
    options.visibleIds
  );
  const targetIds = containerItemIds(
    cards,
    target.stage,
    containerKey,
    options.visibleIds
  );

  const activeIndex = currentIds.indexOf(activeId);
  const workingTargetIds = targetIds.filter((id) => id !== activeId);

  let newIndex: number;
  if (target.overCardId === null) {
    newIndex = workingTargetIds.length;
  } else {
    const overIndex = workingTargetIds.indexOf(target.overCardId);
    if (overIndex === -1) return null;
    newIndex = overIndex;
  }

  let nextIds: string[];
  if (active.status === target.stage && activeGroup === containerKey) {
    if (activeIndex === -1) return null;
    nextIds = arrayMove(
      [...currentIds],
      activeIndex,
      newIndex
    );
  } else {
    nextIds = [
      ...workingTargetIds.slice(0, newIndex),
      activeId,
      ...workingTargetIds.slice(newIndex)
    ];
  }

  if (
    active.status === target.stage &&
    activeGroup === containerKey &&
    activeIndex !== -1 &&
    activeIndex === newIndex
  ) {
    return null;
  }

  return applyContainerOrder(
    cards,
    target.stage,
    containerKey,
    nextIds,
    activeId,
    options.visibleIds
  );
}

/** Final rank + status for persistence after drop. */
export function computeDropPersistence(
  cards: ReadonlyArray<BoardWorkItemRow>,
  cardId: string,
  options: { groupBy: GroupBy; visibleIds: ReadonlySet<string> }
): { status: string; rank: number } | null {
  const card = cards.find((c) => c.id === cardId);
  if (!card) return null;

  const groupKey = containerGroupKey(options.groupBy, card);
  const ordered = cardsInContainer(
    cards,
    card.status,
    groupKey,
    options.visibleIds
  );
  const index = ordered.findIndex((c) => c.id === cardId);
  if (index === -1) return null;

  const prev = index > 0 ? ordered[index - 1] : null;
  const next = index < ordered.length - 1 ? ordered[index + 1] : null;
  const newRank = midpointRank(prev?.rank ?? null, next?.rank ?? null);

  return { status: card.status, rank: newRank };
}

export function cardsNeedPersist(
  before: BoardWorkItemRow,
  after: { status: string; rank: number }
): boolean {
  return before.status !== after.status || before.rank !== after.rank;
}
